import React, { useCallback, useRef } from 'react';
import type { TransformedSimpleRecordDto } from '../../../../api/@types/enhanced-v4-api.types';
import LoadingControls from '../../../../components/common/loading-controls.component';
import { cn } from '../../../../helpers/util';
import GlobeIcon from '../icons/globe-icon.component';

interface RecordsListProps {
  loading: boolean;
  selectedIndex: number;
  records: Array<TransformedSimpleRecordDto>;
  captionFieldId: number;
  searchPerformed: boolean;
  onItemClick: (index: number) => void;
  globalFieldKey: number;
}

/**
 * A component that displays a list of records with search and selection functionality.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {boolean} props.loading - Indicates if the records are being loaded.
 * @param {number} props.selectedIndex - The index of the currently selected record.
 * @param {Array<TransformedSimpleRecordDto>} props.records - Array of records to display.
 * @param {number} props.captionFieldId - The field ID to use as the caption for each record.
 * @param {boolean} props.searchPerformed - Indicates if a search has been performed.
 * @param {(index: number) => void} props.onItemClick - Callback function triggered when a record is clicked.
 * @param {number} props.globalFieldKey - A unique key for identifying global fields.
 *
 * @returns {JSX.Element} A list component that can show a loading state, a "no results" message,
 * or a scrollable list of selectable records.
 *
 * @example
 * <RecordsList
 *   loading={false}
 *   selectedIndex={0}
 *   records={[{ id: 1, fields: { name: 'Record 1' }}]}
 *   captionFieldId={1234}
 *   searchPerformed={true}
 *   onItemClick={(index) => console.log(index)}
 *   globalFieldKey={5678}
 * />
 */

const RecordsList = ({
  loading,
  selectedIndex,
  records,
  captionFieldId,
  searchPerformed,
  onItemClick,
  globalFieldKey,
}: RecordsListProps): React.JSX.Element => {
  const hasSearchResults = !loading && records.length > 0;
  const hasNoSearchResults = !loading && records.length === 0 && searchPerformed;

  return (
    <div
      data-cy="node-record-list"
      className={cn(
        'node-record-list absolute left-0 flex w-full flex-col items-center bg-muted text-foreground shadow-md',
        { '-bottom-6 min-h-6 justify-center': loading || hasNoSearchResults },
        { 'custom-scroll -bottom-[100px] h-[100px] overflow-y-auto': hasSearchResults }
      )}
    >
      {loading && <LoadingControls />}

      {hasNoSearchResults && <div data-cy="node-record-list-no-results">{"Press 'Enter' to create"}</div>}

      {hasSearchResults &&
        records.map((record, index) => (
          <ListItem
            key={record.id}
            index={index}
            isSelected={index === selectedIndex}
            label={record.fields?.[captionFieldId] as string}
            onClick={onItemClick}
            isGlobal={Boolean(record.fields![globalFieldKey])}
          />
        ))}
    </div>
  );
};

export default RecordsList;

// The single list item component
interface ListItemProps {
  index: number;
  label: string;
  isSelected: boolean;
  onClick: (index: number) => void;
  isGlobal: boolean;
}

const ListItem = React.memo(function ListItem({ index, label, isSelected, onClick, isGlobal }: ListItemProps) {
  const itemRef = useRef<HTMLDivElement>(null);

  if (isSelected) {
    itemRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  const handleOnClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      onClick(index);
    },
    [onClick]
  );

  return (
    <div
      data-cy={`node-record-list-item-${index}`}
      ref={itemRef}
      className={cn('hover:bg-muted flex w-full items-center justify-between px-1 py-1 hover:cursor-pointer', {
        'bg-border': isSelected,
      })}
      onClick={handleOnClick}
      tabIndex={-1}
    >
      <span>{label}</span>
      {isGlobal && <GlobeIcon className="text-primary h-3 w-3 leading-4" />}
    </div>
  );
});
